// src/screens/KioskAppointmentDetailScreen.js
import React, { useContext, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import AppHeader from '../components/AppHeader';
import { kioskAPI } from '../services/api';
import useKioskInactivity from '../hooks/useKioskInactivity';

const KioskAppointmentDetailScreen = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  const [appointment, setAppointment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Return to the start screen after 30 seconds without activity.
  useKioskInactivity(30000);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    if (user.role !== 'kiosk') {
      navigate('/');
      return;
    }
  }, [user, navigate]);

  useEffect(() => {
    const fetchAppointment = async () => {
      try {
        setLoading(true);
        setError('');

        const { data } = await kioskAPI.getAppointment(id);

        setAppointment(data);
      } catch (err) {
        console.error('Error loading appointment:', err);
        const msg =
          err.response?.data?.message || 'Failed to load appointment';
        setError(msg);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchAppointment();
    }
  }, [id]);

  if (loading) {
    return (
      <div className="flex flex-col h-full">
        <AppHeader title="Appointment Details" />
        <div className="flex-1 flex items-center justify-center text-app-text-secondary-light dark:text-app-text-secondary text-sm">
          Loading appointment...
        </div>
      </div>
    );
  }

  if (error || !appointment) {
    return (
      <div className="flex flex-col h-full">
        <AppHeader title="Appointment Details" />
        <div className="flex-1 flex flex-col items-center justify-center text-app-text-secondary-light dark:text-app-text-secondary text-sm p-4">
          <p className="mb-4">{error || 'Appointment not found'}</p>
          <button
            onClick={() => navigate('/kiosk/search', { replace: true })}
            className="px-6 py-3 rounded-xl bg-app-accent text-black text-sm font-semibold"
          >
            Back to Search
          </button>
        </div>
      </div>
    );
  }

  const vehicle = appointment.vehicles?.[0];
  const dealer = appointment.dealer;
  const customer = appointment.user;

  const dateStr = appointment.date
    ? new Date(appointment.date).toLocaleDateString(undefined, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : '';

  const getStatusColor = (status) => {
    switch (status) {
      case 'Confirmed':
        return 'bg-app-success text-black';
      case 'Pending':
        return 'bg-yellow-400/90 text-black';
      case 'Vehicle Ready':
        return 'bg-cyan-500/90 text-black';
      case 'Checked In':
        return 'bg-purple-500/90 text-white';
      case 'Completed':
        return 'bg-blue-500/90 text-white';
      case 'Canceled':
        return 'bg-red-500 text-white';
      case 'No Show':
        return 'bg-orange-500/90 text-white';
      default:
        return 'bg-gray-500 text-white';
    }
  };

  return (
    <div className="flex flex-col h-full">
      <AppHeader title="Appointment Details" />

      <div className="flex-1 overflow-y-auto p-4 pb-20 space-y-4 text-sm">
        {/* Status card */}
        <div className="bg-app-bg-secondary-light dark:bg-app-bg-secondary rounded-2xl p-4 border border-app-border-light dark:border-app-border">
          <div className="flex items-center justify-between gap-2 mb-3">
            <div className="text-sm font-semibold text-app-text-light dark:text-app-text">
              Appointment Status
            </div>
            <span
              className={`px-3 py-1 rounded-full text-[11px] font-semibold ${getStatusColor(
                appointment.status
              )}`}
            >
              {appointment.status}
            </span>
          </div>

          <div className="text-xs text-app-text-secondary-light dark:text-app-text-secondary">
            {dateStr} • {appointment.time}
          </div>
        </div>

        {/* Check-in happens on the check-in screen: QR scan or emailed code */}
        <div className="bg-app-bg-secondary-light dark:bg-app-bg-secondary rounded-2xl p-4 border border-app-border-light dark:border-app-border flex flex-col items-center gap-3">
          <div className="text-xs text-app-text-secondary-light dark:text-app-text-secondary text-center">
            {appointment.status === 'Pending' && 'Waiting for dealer confirmation...'}
            {['Confirmed', 'Vehicle Ready'].includes(appointment.status) && 'Ready to check in on the day of the appointment: scan your QR code or use an email code.'}
            {appointment.status === 'Checked In' && 'Customer has checked in for their test drive.'}
            {appointment.status === 'Completed' && 'This test drive has been completed.'}
            {appointment.status === 'Canceled' && 'This appointment has been canceled.'}
            {appointment.status === 'No Show' && 'This appointment was marked as no show.'}
          </div>
          {['Confirmed', 'Vehicle Ready'].includes(appointment.status) && (
            <button
              onClick={() => navigate('/kiosk/checkin', { replace: true })}
              className="w-full py-3 rounded-xl bg-app-accent text-black text-sm font-semibold hover:opacity-90"
            >
              Check In
            </button>
          )}
        </div>

        {/* Customer Info */}
        {customer && (
          <div className="bg-app-bg-secondary-light dark:bg-app-bg-secondary rounded-2xl p-4 border border-app-border-light dark:border-app-border space-y-2">
            <div className="text-sm font-semibold text-app-text-light dark:text-app-text">Customer</div>
            <div className="text-xs text-app-text-secondary-light dark:text-app-text-secondary">{customer.name}</div>
          </div>
        )}

        {/* Vehicle Info */}
        {vehicle && (
          <div className="bg-app-bg-secondary-light dark:bg-app-bg-secondary rounded-2xl p-4 border border-app-border-light dark:border-app-border flex gap-3 items-center">
            <img
              src={vehicle.image}
              alt={`${vehicle.make} ${vehicle.model}`}
              className="w-20 h-16 rounded-xl object-cover flex-shrink-0"
            />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate">
                {vehicle.year} {vehicle.make} {vehicle.model}
              </div>
              {vehicle.type && (
                <div className="text-xs text-app-text-secondary-light dark:text-app-text-secondary">{vehicle.type}</div>
              )}
              {vehicle.price != null && (
                <div className="text-xs text-app-accent mt-1">
                  ${vehicle.price.toLocaleString()}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Dealer Info */}
        {dealer && (
          <div className="bg-app-bg-secondary-light dark:bg-app-bg-secondary rounded-2xl p-4 border border-app-border-light dark:border-app-border space-y-2">
            <div className="text-sm font-semibold text-app-text-light dark:text-app-text">
              {dealer.name}
            </div>
            {dealer.address && (
              <div className="text-xs text-app-text-secondary-light dark:text-app-text-secondary">
                {dealer.address.street && <div>{dealer.address.street}</div>}
                {(dealer.address.city || dealer.address.state || dealer.address.zip) && (
                  <div>
                    {dealer.address.city && `${dealer.address.city}, `}
                    {dealer.address.state} {dealer.address.zip}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Back button */}
        <button
          onClick={() => navigate('/kiosk/search', { replace: true })}
          className="w-full mt-2 py-3 rounded-2xl bg-app-bg-secondary-light dark:bg-app-bg-secondary text-app-text-light dark:text-app-text text-sm font-semibold hover:opacity-90"
        >
          Back to Search
        </button>
      </div>
    </div>
  );
};

export default KioskAppointmentDetailScreen;
